import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AkasSelect from "../../../components/AkasSelect";
import ItemAvatar from "../../../components/ItemAvatar";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import KpiCard from "./KpiCard";
import { buildProductAnalysis, filterProductAnalysis, MENU_STATUSES } from "./menuAnalysisMetrics";

const EMPTY = [];
const qty = (value) => Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const margin = (value) => (value == null ? "—" : `${qty(value)}%`);
const tone = (status) =>
  ({ strong: "success", thin: "warning", promote: "primary", review: "danger" })[status] || "secondary";
const advice = {
  strong: "Pertahankan kualitas dan ketersediaan bahan menu ini.",
  thin: "Menu ini laris, tetapi marginnya di bawah rata-rata menu terjual. Cek harga dan biaya bahan.",
  promote: "Margin menu ini baik, tetapi penjualannya di bawah rata-rata. Coba promosikan.",
  review: "Cek harga jual, diskon, biaya bahan, dan jumlah penjualan menu ini.",
  insufficient: "Data penjualan belum cukup untuk menilai performa menu ini.",
};

const ProductGroupDetail = ({ group, rangeLabel, onClose, onVariantDetail }) => {
  const modalRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previousFocus?.focus?.();
    };
  }, []);
  const onKeyDown = (event) => {
    if (event.key === "Escape") onClose();
    if (event.key !== "Tab") return;
    const buttons = modalRef.current?.querySelectorAll("button:not(:disabled)");
    const first = buttons?.[0];
    const last = buttons?.[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === modalRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  return createPortal(
    <div
      className="modal modal-blur show d-block dashboard-sold-detail-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-analysis-detail-title"
      tabIndex={-1}
      ref={modalRef}
      onKeyDown={onKeyDown}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)", zIndex: 1090 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header dashboard-sold-detail-header">
            <div className="dashboard-sold-detail-identity">
              <ItemAvatar name={group.name} image={group.image} size="lg" />
              <div>
                <span className="dashboard-sold-detail-eyebrow">KEUNTUNGAN MENU</span>
                <h3 className="modal-title" id="product-analysis-detail-title">
                  {group.name}
                </h3>
                <p>{rangeLabel}</p>
              </div>
            </div>
            <button type="button" className="btn-close" aria-label="Tutup detail keuntungan menu" onClick={onClose} />
          </div>
          <div className="modal-body dashboard-sold-detail-body">
            <div className="dashboard-sold-detail-summary">
              <div>
                <span>Terjual</span>
                <strong>{qty(group.total_qty)} item</strong>
              </div>
              <div>
                <span>Total Penjualan</span>
                <strong>{formatIDR(group.total_sales)}</strong>
              </div>
              <div>
                <span>Laba Kotor</span>
                <strong className={group.total_profit < 0 ? "text-danger" : "text-success"}>
                  {formatIDR(group.total_profit)}
                </strong>
              </div>
              <div>
                <span>Margin</span>
                <strong>{margin(group.margin)}</strong>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter mb-0">
                <thead>
                  <tr>
                    <th>Varian terjual</th>
                    <th className="text-end">Terjual</th>
                    <th className="text-end">Penjualan</th>
                    <th className="text-end">HPP</th>
                    <th className="text-end">Laba Kotor</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {group.variants.map((variant) => (
                    <tr key={variant.id}>
                      <td>{variant.variant_label || variant.name}</td>
                      <td className="text-end">{qty(variant.total_qty)}</td>
                      <td className="text-end text-nowrap">{formatIDR(variant.total_sales)}</td>
                      <td className="text-end text-nowrap">{formatIDR(variant.total_cogs)}</td>
                      <td
                        className={`text-end text-nowrap ${variant.total_profit < 0 ? "text-danger" : "text-success"}`}>
                        {formatIDR(variant.total_profit)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => onVariantDetail(variant)}
                          aria-label={`Detail material ${variant.variant_label || variant.name}`}>
                          Material
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-secondary mb-0 mt-3">{advice[group.status]}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ProductAnalysis = ({ data, loading, rangeLabel, onVariantDetail }) => {
  const products = data?.products || EMPTY;
  const analysis = useMemo(() => buildProductAnalysis(products), [products]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("total_profit");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const rows = useMemo(
    () => filterProductAnalysis(analysis.groups, search, status, sort),
    [analysis.groups, search, status, sort],
  );
  useEffect(() => {
    setPage(1);
    setSelected(null);
  }, [data, rangeLabel, search, status, sort]);
  const lastPage = Math.max(1, Math.ceil(rows.length / 10));
  const currentPage = Math.min(page, lastPage);
  const metrics = [
    { title: "Produk Terjual", value: `${qty(analysis.totals.total_qty)} item`, color: "primary" },
    { title: "Penjualan Produk", value: formatIDR(analysis.totals.total_sales), color: "success" },
    {
      title: "Laba Kotor Produk",
      value: formatIDR(analysis.totals.total_profit),
      color: analysis.totals.total_profit < 0 ? "danger" : "success",
    },
  ];
  return (
    <div className="col-12 dashboard-scope-content">
      <div className="row row-cards dashboard-kpi-row dashboard-product-kpis mb-4">
        {metrics.map((metric) => (
          <KpiCard
            key={metric.title}
            {...metric}
            value={data ? metric.value : "—"}
            columnClassName="col-4"
            loading={loading}
            footnote={rangeLabel}
          />
        ))}
      </div>
      <section className="card dashboard-card dashboard-sold-products-card">
        <div className="card-header">
          <div>
            <span className="dashboard-table-eyebrow">PERFORMA MENU</span>
            <h3 className="card-title">Analisis Keuntungan Menu</h3>
          </div>
          <div className="card-actions">
            <span className="badge bg-primary-lt">{analysis.groups.length} menu</span>
          </div>
        </div>
        <div className="card-body pb-3">
          {/* <div className="dashboard-scope-scroll mb-3">
            <div className="akas-scope-filter" role="group" aria-label="Filter performa menu">
              {Object.entries(MENU_STATUSES).map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={status === value}
                  className={`btn btn-sm ${status === value ? "btn-primary" : "btn-white"}`}
                  onClick={() => setStatus(value)}>
                  {label}
                </button>
              ))}
            </div>
          </div> */}
          <div className="dashboard-product-filters">
            <input
              type="search"
              className="form-control"
              placeholder="Cari produk atau varian..."
              aria-label="Cari produk atau varian"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <AkasSelect
              aria-label="Urutkan keuntungan menu"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              options={[
                { value: "total_profit", label: "Laba terbesar" },
                { value: "total_qty", label: "Paling banyak terjual" },
                { value: "total_sales", label: "Penjualan terbesar" },
                { value: "margin", label: "Margin tertinggi" },
              ]}
            />
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-vcenter card-table mb-0 dashboard-product-analysis-table">
            <thead>
              <tr>
                <th>Produk</th>
                <th className="text-end">Terjual</th>
                <th className="text-end">Penjualan</th>
                <th className="text-end">HPP</th>
                <th className="text-end">Laba Kotor</th>
                <th className="text-end">Margin</th>
                <th className="text-end">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }, (_, index) => (
                  <tr key={index}>
                    {Array.from({ length: 7 }, (__, cell) => (
                      <td key={cell}>
                        <div className="placeholder placeholder-lg w-75" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length ? (
                rows.slice((currentPage - 1) * 10, currentPage * 10).map((group) => (
                  <tr key={group.key}>
                    <td>
                      <div className="dashboard-sold-product-name">
                        <ItemAvatar name={group.name} image={group.image} size="md" />
                        <div>
                          <strong>{group.name}</strong>
                          <span className={`badge mt-1 bg-${tone(group.status)}-lt`}>
                            {MENU_STATUSES[group.status]}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="text-end fw-semibold">{qty(group.total_qty)}</td>
                    <td className="text-end">{formatIDR(group.total_sales)}</td>
                    <td className="text-end text-secondary">{formatIDR(group.total_cogs)}</td>
                    <td className={`text-end fw-bold ${group.total_profit < 0 ? "text-danger" : "text-success"}`}>
                      {formatIDR(group.total_profit)}
                    </td>
                    <td className="text-end">{margin(group.margin)}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm dashboard-detail-button"
                        aria-label={`Detail keuntungan ${group.name}`}
                        onClick={() => setSelected(group)}>
                        Detail
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center text-secondary py-5">
                    {data
                      ? products.length
                        ? "Tidak ada produk yang cocok dengan filter."
                        : "Belum ada produk terjual pada periode ini."
                      : "Analisis produk belum tersedia. Perbarui backend lalu muat ulang dashboard."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <span className="text-secondary">{rows.length} menu</span>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-white"
              disabled={loading || currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              aria-label="Halaman sebelumnya">
              ‹
            </button>
            <span>
              {currentPage} / {lastPage}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-white"
              disabled={loading || currentPage >= lastPage}
              onClick={() => setPage(currentPage + 1)}
              aria-label="Halaman berikutnya">
              ›
            </button>
          </div>
        </div>
      </section>
      <details className="mt-3 text-secondary dashboard-product-method">
        <summary>Cara membaca analisis</summary>
        <p className="mt-2 mb-1">
          Penjualan setelah diskon, tanpa pajak. Laba kotor memakai HPP saat transaksi, belum dikurangi biaya
          operasional. Draft dan transaksi dibatalkan tidak dihitung.
        </p>
        <p className="mb-0">
          Label dibandingkan dengan rata-rata menu terjual pada periode ini, bukan standar industri. Penilaian mulai
          dari 3 menu dan 30 item terjual, dengan minimal 5 item per menu. Menu tanpa laba tetap ditandai untuk
          evaluasi.
        </p>
      </details>
      {selected && !loading ? (
        <ProductGroupDetail
          group={selected}
          rangeLabel={rangeLabel}
          onClose={() => setSelected(null)}
          onVariantDetail={(variant) => {
            setSelected(null);
            onVariantDetail(variant);
          }}
        />
      ) : null}
    </div>
  );
};

export default ProductAnalysis;
